import { ACTIVE_COMPANY_KEY } from '../../../lib/ambientHeaders'
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Button from "../../../components/Button/Button"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import Breadcrumbs from "../../../components/BreadCrumbs/Breadcrumbs"
import { fetchCompanyById, updatePaymentSettings } from "../../../features/company/company"
import { StorageGetItem, USER } from "../../../lib/storage"
import { ACH_GATEWAY_OPTIONS, CARD_GATEWAY_OPTIONS, CARD_BACKUP_GATEWAY_OPTIONS } from "./gatewayOptions"

const PaymentProcessingSettings = () => {
  const BreadcrumbsTitleActive = ["Settings"]
  const BreadcrumbsTitle = ["Payment Processing"]

  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const paymentDefaults = useSelector((state) => state.company.paymentDefaults)
  const storedProcessingMode = useSelector((state) => state.company.defaultProcessingMode)

  const [achDefaultGateway, setAchDefaultGateway] = useState('')
  const [cardDefaultGateway, setCardDefaultGateway] = useState('')
  // '' is the "no backup" choice, not an unset control: unlike the two defaults, a company is
  // not required to opt into card failover, and the empty option is how it opts back out.
  const [cardBackupGateway, setCardBackupGateway] = useState('')
  const [achFirstNCount, setAchFirstNCount] = useState('')
  const [achFirstNGateway, setAchFirstNGateway] = useState('')
  // 'review' is both the schema default and what the charge gates resolve an unset mode
  // to, so showing it before the fetch lands can never misstate the effective behavior.
  const [defaultProcessingMode, setDefaultProcessingMode] = useState('review')
  const [firstNError, setFirstNError] = useState('')
  const [cardBackupError, setCardBackupError] = useState('')
  const companyName = useSelector((state) => state.company.company?.name)

  // Mirrors CompanySettings.jsx: resolve the current user's company then dispatch
  // fetchCompanyById on mount. The company slice's paymentDefaults (populated by
  // Task 7's fetchCompanyById/updatePaymentSettings reducers) is read live via
  // useSelector below, so this fetch is a refresh — it doesn't gate the initial
  // display of whatever paymentDefaults is already in the store.
  useEffect(() => {
    const getUserCompany = async () => {
      const user = await StorageGetItem(USER)
      // The ACTIVE brand, not the caller's home firm. These two screens read and write a company
      // record; keyed on user.company they would show and edit the other firm's settings after a
      // switch, which is the one place getting it wrong writes to the wrong firm rather than merely
      // reading from it.
      const company = localStorage.getItem(ACTIVE_COMPANY_KEY) || user?.company

      if (company) {
        dispatch(fetchCompanyById(company))
      }
    }
    getUserCompany()
  }, [dispatch])

  // Keep the local (editable) select values in sync with the redux-held defaults
  // whenever they change (initial load or a fresh fetch/save response).
  useEffect(() => {
    if (paymentDefaults) {
      setAchDefaultGateway(paymentDefaults.achDefaultGateway || '')
      setCardDefaultGateway(paymentDefaults.cardDefaultGateway || '')
      setCardBackupGateway(paymentDefaults.cardBackupGateway || '')
      setAchFirstNCount(paymentDefaults.achFirstNCount == null ? '' : String(paymentDefaults.achFirstNCount))
      setAchFirstNGateway(paymentDefaults.achFirstNGateway || '')
    }
  }, [paymentDefaults])

  useEffect(() => {
    if (storedProcessingMode) {
      setDefaultProcessingMode(storedProcessingMode)
    }
  }, [storedProcessingMode])

  const handleSave = async () => {
    // Mirrors the rule the server enforces (validatePaymentDefaults): a backup equal to the
    // primary would fail the charge over to the gateway that just declined it, which
    // accomplishes nothing. Checked here only so the message is immediate — the server stays
    // the authority.
    if (cardBackupGateway && cardBackupGateway === cardDefaultGateway) {
      setCardBackupError('The card backup gateway must differ from the card default gateway.')
      return
    }
    setCardBackupError('')

    // All-or-nothing, mirroring the rule the server enforces (validateAchFirstN): a count with
    // no gateway names no destination, a gateway with no count names no window. Checked here
    // only so the message is immediate — the server stays the authority.
    const countGiven = achFirstNCount !== ''
    const gatewayGiven = achFirstNGateway !== ''
    if (countGiven !== gatewayGiven) {
      setFirstNError('Set both the number of payments and the gateway, or leave both empty.')
      return
    }
    if (countGiven && (!Number.isInteger(Number(achFirstNCount)) || Number(achFirstNCount) <= 0)) {
      setFirstNError('The number of payments must be a whole number greater than zero.')
      return
    }
    setFirstNError('')

    triggerLoading(true)
    try {
      // BOTH first-N fields go on EVERY save. The endpoint treats an omitted pair as an
      // instruction to clear the window, and this screen never used to send them — so saving
      // any unrelated setting here silently erased a configured first-N window, leaving the
      // position rule reachable only through direct API calls.
      // cardBackupGateway travels on every save for exactly the same reason: the PUT replaces
      // the whole paymentDefaults subdocument, so omitting it erased a backup set out-of-band.
      // '' is normalized to null — the server's nullish check reads null as "no backup", while
      // '' would be measured against the Card allow-list and rejected.
      const result = await dispatch(updatePaymentSettings({
        achDefaultGateway,
        cardDefaultGateway,
        cardBackupGateway: cardBackupGateway || null,
        achFirstNCount: countGiven ? Number(achFirstNCount) : null,
        achFirstNGateway: gatewayGiven ? achFirstNGateway : null,
        defaultProcessingMode,
      }))
      if (!result.error) {
        showSnackBar("success", "Payment processing settings successfully updated")
      } else {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Failed to update payment processing settings')
      }
    } catch (error) {
      showSnackBar("error", error?.message || 'Failed to update payment processing settings')
    }
    triggerLoading(false)
  }

  return (
    <div>
      <div>
        <h1 className="font-bold text-xl mb-[8px] w-full">
          Payment Processing
        </h1>
        <Breadcrumbs title={BreadcrumbsTitle} titleActive={BreadcrumbsTitleActive} />
        <p className="text-text-secondary text-sm mt-2">
          These settings govern payments belonging to {companyName || 'your company'}. Clients of
          another company are routed by that company's own settings.
        </p>
      </div>

      <div className="mt-10 px-6 py-5 rounded-2xl shadow-lg border border-divider">
        <div className="flex flex-col gap-6">
          <div>
            <p className="text-sm font-medium mb-2">ACH Default Gateway</p>
            <Select
              fullWidth
              size="small"
              value={achDefaultGateway}
              onChange={(e) => setAchDefaultGateway(e.target.value)}
              inputProps={{ 'aria-label': 'ACH Default Gateway' }}
              data-testid="ach-default-gateway-select"
            >
              {ACH_GATEWAY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <p className="text-text-secondary text-sm mt-2">
              Gateway used to process ACH (bank account) payments unless overridden per client.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Card Default Gateway</p>
            <Select
              fullWidth
              size="small"
              value={cardDefaultGateway}
              onChange={(e) => setCardDefaultGateway(e.target.value)}
              inputProps={{ 'aria-label': 'Card Default Gateway' }}
              data-testid="card-default-gateway-select"
            >
              {CARD_GATEWAY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <p className="text-text-secondary text-sm mt-2">
              Gateway used to process card payments unless overridden per client.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Card Backup Gateway</p>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={cardBackupGateway}
              onChange={(e) => setCardBackupGateway(e.target.value)}
              inputProps={{ 'aria-label': 'Card Backup Gateway' }}
              data-testid="card-backup-gateway-select"
            >
              <MenuItem value="">No backup gateway</MenuItem>
              {CARD_BACKUP_GATEWAY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {cardBackupError && (
              <p className="text-error-main text-sm mt-2" data-testid="card-backup-gateway-error">{cardBackupError}</p>
            )}
            <p className="text-text-secondary text-sm mt-2">
              Where a card charge is retried automatically when the card default gateway soft
              declines it. Must differ from the card default. Leave empty for no failover.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Default Processing Mode</p>
            <Select
              fullWidth
              size="small"
              value={defaultProcessingMode}
              onChange={(e) => setDefaultProcessingMode(e.target.value)}
              inputProps={{ 'aria-label': 'Default Processing Mode' }}
              data-testid="default-processing-mode-select"
            >
              <MenuItem value="review">Review — payments wait for daily operator approval</MenuItem>
              <MenuItem value="auto">Auto — the scheduler charges due payments unattended</MenuItem>
            </Select>
            <p className="text-text-secondary text-sm mt-2">
              Applies to billing accounts without a mode of their own. A per-account setting on
              the client's Billing tab always overrides this.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">First payments of a plan (ACH)</p>
            <div className="flex gap-4">
              <TextField
                size="small"
                type="number"
                label="Number of payments"
                value={achFirstNCount}
                onChange={(e) => setAchFirstNCount(e.target.value)}
                inputProps={{ min: 1, 'aria-label': 'ACH first payments count' }}
                data-testid="ach-first-n-count"
              />
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={achFirstNGateway}
                onChange={(e) => setAchFirstNGateway(e.target.value)}
                inputProps={{ 'aria-label': 'ACH first payments gateway' }}
                data-testid="ach-first-n-gateway-select"
              >
                <MenuItem value="">No positional preference</MenuItem>
                {ACH_GATEWAY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            {firstNError && (
              <p className="text-error-main text-sm mt-2" data-testid="ach-first-n-error">{firstNError}</p>
            )}
            <p className="text-text-secondary text-sm mt-2">
              Routes the opening ACH payments of a plan through a different gateway. Leave both
              empty for no positional preference.
            </p>
          </div>
        </div>

        <div className="flex justify-end items-center gap-4 self-stretch py-6 border-t border-divider mt-6">
          <Button size="lg" width="w-[156px]" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PaymentProcessingSettings
